/**
 * 어드민 회원 관리(/admin/users) 할 일 대기열 계산.
 *
 * 기획: docs/ops/admin-users-redesign-plan.md
 *
 * 이 화면에서 운영팀이 실제로 눌러야 하는 단계는 3개다.
 *   02 관리자 승인   — user.verified (승인하면 로그인 차단도 함께 풀린다)
 *   06 수락 확인     — channel.viewer_accepted_at
 *   07 통화(KRW) 확인 — channel.currency_confirmed_at
 * 06·07 은 채널 쪽 조건만으로 정확히 뽑히므로 조인 없이 서버 쿼리로 셀 수 있다.
 * 채널 미등록·스튜디오 대기·완료는 user × channel 조인이 필요해 서버 RPC(admin_user_onboarding_stage_*)
 * 가 센다 — 판정 규칙은 studioConnect.onboardingBadge 와 SQL 이 같아야 한다.
 * 로그인 가능 여부(auth.users.banned_until)도 RPC(admin_user_login_status)로 읽는다 — 기획 3단계.
 *
 * 날짜 파싱은 채널 화면과 같은 문제를 겪는다(text 컬럼 · 표기 혼재) — parseChannelDate 를 그대로 쓴다.
 */
import { parseChannelDate } from "./adminChannelQueue";
import { tierNameFromBand } from "./creatorTierMetrics";
import {
  CREATOR_TIER_NAMES,
  CREATOR_TIER_CUT_LABELS,
  ROOKIE_LABEL,
  DORMANT_LABEL,
  tierIndex,
} from "./creatorTierNames";

/** 가입 후 지난 일수. 날짜를 못 읽으면 null. */
/** 파트너 초대 링크(/signup?as=partner)로 신청한 회원 — 승인 단추가 「파트너로 승인」이 된다 (2026-09-11) */
export function isPartnerApplicant(user) {
  return Boolean(user) && user.signup_intent === "partner";
}

/** 승인 단추 라벨 */
export function approveLabel(user) {
  return isPartnerApplicant(user) ? "파트너로 승인" : "승인";
}

/** 「파트너로 승인」이 주는 역할 — 파트너도 일반 회원(크리에이터)처럼 활동한다(대표 결정 2026-09-28, docs/ops/partner-works-browse-plan.md §4-1).
 *  Partner 단독을 주면 ProtectedRoute 가 파트너센터 밖을 잠근다(isPartnerOnly) — 그건 회원 편집에서 일부러 고를 때만. */
export const PARTNER_APPROVE_ROLE = "Creator, Partner";

/**
 * 승인 update 값. asPartner 면 role='Creator, Partner' 를 함께 쓴다(어드민만 role 변경 가능 — user_role_company_guard).
 * 🔴 verified + approved_at 동시 기록은 종전 규칙 그대로(한쪽만 수정 금지).
 */
export function approvePatch({ asPartner = false, now = new Date() } = {}) {
  const patch = { verified: true, approved_at: new Date(now).toISOString() };
  return asPartner ? { ...patch, role: PARTNER_APPROVE_ROLE } : patch;
}

export function waitingDays(user, now = Date.now()) {
  const at = parseChannelDate(user?.created_date);
  if (!at) return null;
  const diff = now - at.getTime();
  if (diff < 0) return 0;
  return Math.floor(diff / 86400000);
}

/** 대기열 정렬. 기본은 오래 기다린 순 — 밀린 건부터 지운다. 날짜 없는 건은 뒤로. */
export function sortUserQueue(users, mode = "oldest") {
  const list = Array.isArray(users) ? [...users] : [];
  const key = (u) => {
    const at = parseChannelDate(u?.created_date);
    return at ? at.getTime() : null;
  };
  return list.sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    if (ka === null && kb === null) return 0;
    if (ka === null) return 1;
    if (kb === null) return -1;
    return mode === "newest" ? kb - ka : ka - kb;
  });
}

/**
 * 06 수락 확인이 필요한 채널.
 * 크리에이터가 「초대했다」고 자기신고(viewer_invited_at)했는데 운영이 아직 확인하지 않은 상태.
 */
export function needsAcceptConfirm(channel) {
  return Boolean(channel?.viewer_invited_at) && !channel?.viewer_accepted_at;
}

/**
 * 07 통화(KRW) 확인이 필요한 채널.
 * 수락이 확인된 뒤라야 판정할 수 있다 — 수락 전에는 확인 대상이 아니다.
 */
export function needsCurrencyConfirm(channel) {
  return Boolean(channel?.viewer_accepted_at) && !channel?.currency_confirmed_at;
}

/** 한 사용자의 채널들에서 남은 확인 건수 */
export function pendingConfirmCounts(channels) {
  const list = Array.isArray(channels) ? channels : [];
  return {
    accept: list.filter(needsAcceptConfirm).length,
    currency: list.filter(needsCurrencyConfirm).length,
  };
}

/** 행 목록에서 이메일만 중복 없이 (소문자 비교는 하지 않는다 — channel.user 는 user.email 원본을 담는다) */
export function uniqueEmails(rows, field = "user") {
  return [...new Set((rows || []).map((r) => r?.[field]).filter(Boolean))];
}

/* ── 3단계 · 로그인 가능 여부 (RPC admin_user_login_status / admin_login_blocked_users) ───── */

/** PostgREST 가 함수를 못 찾을 때의 코드 — 마이그레이션 미적용. 화면은 이걸 '확인 불가' 로 흡수한다. */
export const RPC_MISSING_CODE = "PGRST202";

export function isRpcMissing(error) {
  return Boolean(error) && error.code === RPC_MISSING_CODE;
}

/**
 * RPC 행 → 이메일별 로그인 상태.
 * auth.users 에 행이 없는 이메일은 RPC 결과에 나오지 않는다 — 요청한 이메일이면 hasAuth:false 로 채운다
 * (레거시 이관 계정: 첫 로그인 때 자동 signUp — docs/pages/public/Login.md).
 */
export function buildLoginMap(rows, requestedEmails = []) {
  const map = {};
  (requestedEmails || []).forEach((email) => {
    if (email) map[email] = { hasAuth: false, blocked: false, bannedUntil: null, lastSignInAt: null };
  });
  (rows || []).forEach((r) => {
    if (!r?.email) return;
    map[r.email] = {
      hasAuth: true,
      blocked: Boolean(r.login_blocked),
      bannedUntil: r.banned_until || null,
      lastSignInAt: r.last_sign_in_at || null,
    };
  });
  return map;
}

/**
 * 계정 상태 한 줄 — 배지용.
 * `verified` 는 "관리자 승인" 만 말한다. 실제 로그인 가능 여부는 login(RPC 결과)이 있어야 안다.
 *  · 미승인             → 「미승인 · 로그인 차단」 (2026-07 일괄 차단분이 그 상태이고 승인 버튼이 함께 푼다)
 *  · 승인 + 차단 남음   → 「승인됨 · 로그인 차단」 (SQL 로만 풀던 경우 — 차단 해제 버튼 대상)
 *  · 승인               → 「승인됨」
 */
export function accountStatus(user, login) {
  if (!user?.verified) {
    return { key: "pending", label: "미승인 · 로그인 차단", tone: "red" };
  }
  if (login?.blocked) {
    return { key: "blocked", label: "승인됨 · 로그인 차단", tone: "red" };
  }
  return { key: "verified", label: "승인됨", tone: "green" };
}

/** 승인 날짜 짧은 표기 — 올해면 「9/15」, 다른 해면 「2025.11.3」. 못 읽으면 null. */
function shortApprovedDay(value, now) {
  const at = parseChannelDate(value);
  if (!at) return null;
  const sameYear = at.getFullYear() === new Date(now).getFullYear();
  return sameYear
    ? `${at.getMonth() + 1}/${at.getDate()}`
    : `${at.getFullYear()}.${at.getMonth() + 1}.${at.getDate()}`;
}

/**
 * 표 「가입 승인」 열 · 카드 배지 (2026-10-05 대표 요청 — 승인 여부를 목록에서 한 번에).
 * 상태(key·label·tone)는 accountStatus 와 같다. sub = 표 칸 아래 줄(승인 날짜 / 기다린 기간),
 * short = 카드 배지 한 줄. rank = 「가입 승인」 열 오름차순 정렬 순서(미승인 → 승인됐는데 차단 → 승인됨).
 */
export function approvalCell(user, login, now = Date.now()) {
  const status = accountStatus(user, login);
  if (status.key === "pending") {
    const days = waitingDays(user, now);
    const wait = days === null ? "가입일 없음" : days <= 0 ? "오늘 가입" : `${days}일째 대기`;
    return { key: "pending", label: "미승인", tone: "red", sub: wait, short: `미승인 · ${wait}`, rank: 0 };
  }
  const day = shortApprovedDay(user.approved_at, now);
  if (status.key === "blocked") {
    return {
      key: "blocked",
      label: "승인 · 로그인 차단",
      tone: "red",
      sub: day ? `${day} 승인` : "승인일 기록 없음",
      short: "승인 · 로그인 차단",
      rank: 1,
    };
  }
  return {
    key: "verified",
    label: "승인됨",
    tone: "green",
    sub: day ? `${day} 승인` : "승인일 기록 없음",
    short: day ? `승인 ${day}` : "승인됨",
    rank: 2,
  };
}

/** 상세 패널 「로그인」 줄. login 이 없으면(RPC 미적용·실패) 단정하지 않는다. */
export function loginStatusLabel(user, login) {
  if (!login) {
    return { key: "unknown", label: "확인 불가", tone: "neutral" };
  }
  if (login.blocked) {
    return { key: "blocked", label: "차단됨 · 해제 필요", tone: "red" };
  }
  if (!user?.verified) {
    return { key: "not_verified", label: "승인 전 (불가)", tone: "red" };
  }
  if (!login.hasAuth) {
    return { key: "no_auth", label: "가능 · 첫 로그인 때 계정 생성", tone: "green" };
  }
  return { key: "ok", label: "가능", tone: "green" };
}

/** 승인됐는데 차단이 남아 있어 「차단 해제」 버튼을 보여 줄 대상인지 */
export function canUnban(user, login) {
  return Boolean(user?.verified && login?.blocked);
}

/* ── 1단계 잔여 · 온보딩 단계별 인원 (RPC admin_user_onboarding_stage_*) ──────────────── */

/**
 * 「전체 회원」 옆 칩. 키·순서는 studioConnect.onboardingBadge 의 판정 키와 같다.
 * legacy('-') 는 온보딩 대상이 아니라 칩으로 두지 않는다.
 */
export const ONBOARDING_STAGES = [
  { key: "no_channel", label: "채널 미등록" },
  { key: "studio_wait", label: "스튜디오 대기" },
  { key: "done", label: "온보딩 완료" },
];

export const ONBOARDING_STAGE_KEYS = ONBOARDING_STAGES.map((s) => s.key);

export function isOnboardingStage(mode) {
  return ONBOARDING_STAGE_KEYS.includes(mode);
}

/** RPC (stage, cnt) 행 → { no_channel: n, … }. 결과에 없는 단계는 0. */
export function stageCountsToMap(rows) {
  const map = {};
  ONBOARDING_STAGE_KEYS.forEach((k) => {
    map[k] = 0;
  });
  (rows || []).forEach((r) => {
    if (r?.stage && r.stage in map) map[r.stage] = Number(r.cnt) || 0;
  });
  return map;
}

/**
 * 단계 목록 RPC 는 user 행(jsonb)에 total_count 를 얹어 준다 — 둘을 가른다.
 * @returns {{ users: object[], total: number }}
 */
export function splitStageUsers(rows) {
  const list = Array.isArray(rows) ? rows : [];
  let total = 0;
  const users = list.map((row) => {
    const { total_count, ...user } = row || {};
    if (total === 0 && Number(total_count) > 0) total = Number(total_count);
    return user;
  });
  return { users, total };
}

/* ── 2026-09-06 회원 목록 표(시안 B) 확장 열 ─────────────────────────────
 * 기획: docs/ops/admin-users-list-columns-plan.md. 전부 페이지 안(최대 20명) 계산이며 서버 정렬·필터가 아니다.
 */

const PLATFORM_OF_TYPE = { KAKAO: "kakao", NAVER: "naver" };

/** 어떤 날짜 컬럼이든 지난 일수. 못 읽으면 null. */
export function daysSince(value, now = Date.now()) {
  const at = parseChannelDate(value);
  if (!at) return null;
  const diff = now - at.getTime();
  return diff < 0 ? 0 : Math.floor(diff / 86400000);
}

/** 채널 칸 — `승인/등록` + 플랫폼 목록(유튜브·카카오·네이버, 있는 것만, 등장 순). */
export function channelSummary(channels) {
  const list = Array.isArray(channels) ? channels : [];
  const platforms = [];
  let approved = 0;
  list.forEach((ch) => {
    const yn = ch?.approved_yn;
    if (yn === true || yn === "true" || yn === "Y") approved += 1;
    const p = PLATFORM_OF_TYPE[String(ch?.type || "").toUpperCase()] || "youtube";
    if (!platforms.includes(p)) platforms.push(p);
  });
  return { total: list.length, approved, platforms };
}

/**
 * 온보딩 점 2개 — 앱 · 스튜디오(뷰어 수락 + 통화 확인). 2026-10-30 단톡방 점 삭제(가입 흐름에서 단톡방 단계를 뺐다).
 * on = 끝남, half = 자기신고만/일부 채널만, off = 아직.
 */
export function onboardingDots({ user, channels, appStatus }) {
  const list = Array.isArray(channels) ? channels : [];
  const app =
    appStatus?.key === "installed" ? "on" : appStatus?.key === "self_reported" ? "half" : "off";
  const n = list.length;
  const confirmed = list.filter((c) => c?.currency_confirmed_at).length;
  const accepted = list.filter((c) => c?.viewer_accepted_at).length;
  const studio =
    n === 0 ? "off" : confirmed === n ? "on" : accepted > 0 || confirmed > 0 ? "half" : "off";
  const label = [
    appStatus?.label ? `앱 ${appStatus.label}` : "앱 미설치",
    n === 0 ? "채널 없음" : `스튜디오 ${confirmed}/${n}`,
  ].join(" · ");
  return {
    dots: [
      { key: "app", state: app },
      { key: "studio", state: studio },
    ],
    label,
  };
}

/**
 * 등급·정산 정보 맵을 **못 읽었을 때**의 행 — 「행 없음」(산정 전·미등록)과 구분한다(2026-09-29).
 *   맵이 null 이면 rowOf 가 이것을 돌려주고, 칸은 「확인 불가」 · 그 열 필터는 잠근다 · 정렬은 뒤로.
 *   조회 실패를 실제 값(전원 「산정 전」·「정산 미등록」)처럼 보이게 하던 문제.
 */
export const ROW_UNAVAILABLE = Object.freeze({ __unavailable: true });

/** 이메일별 맵에서 한 회원의 행 — 맵이 null(조회 실패)이면 ROW_UNAVAILABLE, 없으면 null */
export function rowOf(map, email) {
  if (map === null) return ROW_UNAVAILABLE;
  return (map && email ? map[email] : null) ?? null;
}

/**
 * 등급 칸 — creator_tier_monthly 최신 월 행 하나로 판정.
 * 휴면은 어드민 화면이므로 표기하되 동결 등급은 `frozen` 에만 둔다(툴팁용). 행이 없으면 「산정 전」.
 */
export function tierCell(row) {
  if (row === ROW_UNAVAILABLE) return { kind: "unavailable", label: "—", sub: "확인 불가", tierName: null };
  if (!row) return { kind: "none", label: "—", sub: "산정 전", tierName: null };
  const tierName = tierNameFromBand(row.band);
  if (row.is_dormant) {
    return { kind: "dormant", label: DORMANT_LABEL, sub: "90일 무활동", tierName: null, frozen: tierName };
  }
  if (row.status === "rookie") {
    const current = row.rookie_progress_current ?? 0;
    const target = row.rookie_progress_target ?? 5;
    return {
      kind: "rookie",
      label: ROOKIE_LABEL,
      sub: `작품 영상 ${current}/${target}`,
      tierName: null,
      progress: { current, target },
    };
  }
  if (!tierName) return { kind: "none", label: "—", sub: "산정 전", tierName: null };
  return { kind: "tier", label: tierName, sub: CREATOR_TIER_CUT_LABELS[tierName] || "", tierName };
}

/** 정산 정보 칸 — payout_info 행 유무·type. */
export function payoutCell(row) {
  if (row === ROW_UNAVAILABLE) return { registered: null, label: "확인 불가", type: null, unavailable: true };
  if (!row) return { registered: false, label: "미등록", type: null };
  return { registered: true, label: row.type === "개인" ? "개인" : "사업자", type: row.type || null };
}

/**
 * 「본인확인」 열 (2026-10-09, 기존 회원 소급 PR-1 · 기획 docs/ops/identity-existing-members-plan.md §7·§12)
 *   user.identity_verified_at 로 확인 완료/미확인 · payoutRow(정산 정보 행)의 type 이 「일반/법인 사업자」면 「사업자」 배지.
 *   🔒 판정은 서버 값만 — 화면이 인증 여부를 계산하지 않는다. 사업자 구분은 정산 정보에서만 온다(가입 때는 받지 않는다).
 */
export function identityCell(user, payoutRow) {
  const at = user && user.identity_verified_at ? new Date(user.identity_verified_at) : null;
  const verified = Boolean(at && !Number.isNaN(at.getTime()));
  const business = Boolean(payoutRow && payoutRow !== ROW_UNAVAILABLE && payoutRow.type === "일반/법인 사업자");
  return {
    verified,
    business,
    label: verified ? "확인 완료" : "미확인",
    date: verified ? `${at.getMonth() + 1}/${at.getDate()}` : null,
    title: verified
      ? `휴대폰 본인확인 ${at.toLocaleString("ko-KR")}`
      : "휴대폰 본인확인을 아직 안 했어요 — 홈 띠·설정 › 본인확인에서 안내 중(기획 §4·§9)",
  };
}

export const IDENTITY_FILTER_OPTIONS = [
  ["all", "전체"],
  ["verified", "확인 완료"],
  ["unverified", "미확인"],
];

export const TIER_FILTER_OPTIONS = [
  ["all", "전체"],
  ["rookie", ROOKIE_LABEL],
  ...CREATOR_TIER_NAMES.map((n) => [n, n]),
  ["dormant", DORMANT_LABEL],
  ["none", "산정 전"],
];

export function matchesTierFilter(cell, filter) {
  if (!filter || filter === "all") return true;
  if (filter === "rookie" || filter === "dormant" || filter === "none") return cell.kind === filter;
  return cell.kind === "tier" && cell.tierName === filter;
}

/**
 * 표 열 머리 정렬(페이지 안). key: wait(가입일) · channels(등록 채널 수) · tier(등급 순) ·
 * approval(가입 승인 — 오름차순이면 미승인이 위로 · ctx.loginByEmail 로 「승인됐는데 차단」 구분).
 * 값이 없는 행은 방향과 무관하게 뒤로, 같은 값은 원래 순서 유지.
 */
export function sortUsersByColumn(users, sort, ctx = {}) {
  const list = Array.isArray(users) ? [...users] : [];
  if (!sort || !sort.key) return list;
  const dir = sort.dir === "asc" ? 1 : -1;
  const valueOf = (u) => {
    switch (sort.key) {
      case "wait": {
        const at = parseChannelDate(u?.created_date);
        return at ? at.getTime() : null;
      }
      case "approval":
        return approvalCell(u, ctx.loginByEmail?.[u?.email]).rank;
      case "channels":
        return channelSummary(ctx.channelsByEmail?.[u?.email]).total;
      case "tier": {
        const c = tierCell(rowOf(ctx.tierByEmail, u?.email));
        if (c.kind === "tier") return tierIndex(c.tierName);
        if (c.kind === "rookie") return -1;
        if (c.kind === "dormant") return -2;
        return null;
      }
      default:
        return null;
    }
  };
  return list
    .map((u, i) => ({ u, i, v: valueOf(u) }))
    .sort((a, b) => {
      if (a.v === null && b.v === null) return a.i - b.i;
      if (a.v === null) return 1;
      if (b.v === null) return -1;
      if (a.v === b.v) return a.i - b.i;
      return (a.v - b.v) * dir;
    })
    .map((x) => x.u);
}
